import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, changePassword, checkAuth } = useAuth();

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(!!user?.isTwoFactorEnabled);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await changePassword({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });

    setLoading(false);

    if (result.success) {
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  };

  const handleStartTwoFactorSetup = async () => {
    setTwoFALoading(true);
    try {
      const response = await authAPI.setup2FA();
      const { qrCodeUrl, secret } = response.data.data;
      setQrCodeUrl(qrCodeUrl);
      setManualSecret(secret);
      setOtpToken('');
      setSetupDialogOpen(true);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to start 2FA setup';
      toast.error(message);
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleConfirmEnableTwoFactor = async () => {
    if (otpToken.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setTwoFALoading(true);
    try {
      await authAPI.enable2FA(otpToken);
      toast.success('Two-factor authentication enabled');
      setIsTwoFactorEnabled(true);
      setSetupDialogOpen(false);
      setOtpToken('');
      setQrCodeUrl('');
      setManualSecret('');
      if (checkAuth) await checkAuth();
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid verification code';
      toast.error(message);
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleConfirmDisableTwoFactor = async () => {
    if (!disablePassword) {
      toast.error('Enter your password to disable 2FA');
      return;
    }
    setTwoFALoading(true);
    try {
      await authAPI.disable2FA(disablePassword);
      toast.success('Two-factor authentication disabled');
      setIsTwoFactorEnabled(false);
      setDisableDialogOpen(false);
      setDisablePassword('');
      if (checkAuth) await checkAuth();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to disable 2FA';
      toast.error(message);
    } finally {
      setTwoFALoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* User Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1">{user?.username}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{user?.email}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role
              </Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                {user?.role}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Screen Size
              </Typography>
              <Typography variant="body1">
                {user?.screenSize?.label || user?.screenSize ? `${user.screenSize.width}x${user.screenSize.height}` : 'Not set'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Account Status
              </Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                {user?.status}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body1">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box component="form" onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                margin="normal"
                disabled={loading}
              />

              <TextField
                fullWidth
                type="password"
                label="New Password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                margin="normal"
                disabled={loading}
                helperText="Minimum 6 characters"
              />

              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                margin="normal"
                disabled={loading}
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Two-Factor Authentication */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Two-Factor Authentication
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Alert severity={isTwoFactorEnabled ? 'success' : 'info'} sx={{ mb: 2 }}>
              {isTwoFactorEnabled
                ? 'Two-factor authentication is enabled on your account.'
                : 'Add an extra layer of security by requiring a code from an authenticator app at login.'}
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={isTwoFactorEnabled}
                  disabled={twoFALoading}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleStartTwoFactorSetup();
                    } else {
                      setDisableDialogOpen(true);
                    }
                  }}
                />
              }
              label={isTwoFactorEnabled ? 'Enabled' : 'Disabled'}
            />
          </Paper>
        </Grid>

        {/* Display URL - Hidden for regular users */}
        {/* <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Display URL
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Alert severity="info" sx={{ mb: 2 }}>
              Use this URL to display your approved content on LED screens
            </Alert>

            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              {user?.screenMediaUrl ? `${process.env.REACT_APP_API_URL}/api/display/${user.screenMediaUrl}` : 'Loading...'}
            </Box>
          </Paper>
        </Grid> */}
      </Grid>
      <Dialog open={setupDialogOpen} onClose={() => setSetupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy), then enter the 6-digit code it generates.
          </Typography>
          {qrCodeUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={qrCodeUrl} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
            </Box>
          )}
          {manualSecret && (
            <Alert severity="info" sx={{ mb: 2, wordBreak: 'break-all' }}>
              Can't scan? Enter this code manually: <strong>{manualSecret}</strong>
            </Alert>
          )}
          <TextField
            fullWidth
            label="Verification Code"
            value={otpToken}
            onChange={(e) => setOtpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            disabled={twoFALoading}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)} disabled={twoFALoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmEnableTwoFactor}
            disabled={twoFALoading || otpToken.length !== 6}
          >
            {twoFALoading ? 'Verifying...' : 'Enable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm disabling two-factor authentication.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            disabled={twoFALoading}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)} disabled={twoFALoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDisableTwoFactor}
            disabled={twoFALoading || !disablePassword}
          >
            {twoFALoading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;